import type { ReactElement, ReactNode } from 'react';
import { Route } from 'react-router-dom';

import type { NavItemConfig, RouteNavConfig } from '@revelio/auth';

import { derivePageId } from '../../hooks/derive-page-id';
import {
  APP_PAGE_TYPE,
  usePageVisitTracking,
} from '../../hooks/usePageVisitTracking';
import { RequireAuth } from './ProtectedRoute';

export const flattenNavRouteItems = (
  items: RouteNavConfig[]
): RouteNavConfig[] => {
  return items.reduce((acc, item) => {
    if (item.children && Array.isArray(item.children)) {
      // A whole section is marked once: the group's `pageVisit` applies to
      // every route under it unless the route says otherwise.
      const children = item.pageVisit
        ? item.children.map((child) => ({
            pageVisit: item.pageVisit,
            ...child,
          }))
        : item.children;
      return [...acc, ...flattenNavRouteItems(children)] as RouteNavConfig[];
    }
    return [...acc, item] as RouteNavConfig[];
  }, [] as RouteNavConfig[]);
};

/**
 * `page visit` for a config route. Keyed on the route's own path pattern, not
 * the pathname, so stepping through nested routes (the Data Builder wizard,
 * a report) stays one visit of the page they belong to.
 */
const RouteVisitTracker = ({
  page,
  path,
  children,
}: {
  page: string;
  path: string;
  children: ReactNode;
}) => {
  usePageVisitTracking({
    page,
    pageId: derivePageId(path),
    pageType: APP_PAGE_TYPE,
    path: path.startsWith('/') ? path : `/${path}`,
  });
  return <>{children}</>;
};

/**
 * A route reports a visit unless the page reports its own, or there is
 * nothing to report it as: no name to send as `page`, or a path that derives
 * no `page_id` (all params).
 */
export const routeReportsVisit = (
  r: RouteNavConfig | NavItemConfig
): r is RouteNavConfig & { name: string; path: string } =>
  !r.pageVisit && !!r.name && !!r.path && !!derivePageId(r.path);

const routeElement = (r: RouteNavConfig | NavItemConfig): ReactNode =>
  routeReportsVisit(r) ? (
    <RouteVisitTracker page={r.name} path={r.path}>
      {r.element}
    </RouteVisitTracker>
  ) : (
    r.element
  );

export const reducedRoutes = (navItems: (RouteNavConfig | NavItemConfig)[]) =>
  flattenNavRouteItems(navItems).reduce<ReactElement[]>((routes, r, i) => {
    if (!r.element) {
      return routes;
    }
    const hasNestedRoutes = !!r.nestedRoutes?.length;
    routes.push(
      <Route
        key={i}
        path={r.path}
        element={
          <RequireAuth
            tab={r.serverTab}
            adminRequired={r.admin}
            clientAdminRequired={r.clientAdmin}
          >
            {routeElement(r)}
          </RequireAuth>
        }
      >
        {hasNestedRoutes &&
          r.nestedRoutes?.map((nr, j) => (
            <Route key={j} path={nr.path} element={nr.element} />
          ))}
      </Route>
    );
    return routes;
  }, []);
